import {
  useState,
  useEffect,
  // ChangeEvent,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Spin } from "antd";
import CrystalDraw, {
  setColor,
  setTool,
  setHighlighterColor,
  setLineWidth,
  undo,
  redo,
  removeBoard,
  uploadPDF,
  exportPDF,
  useBoundStore,
  setCurrentBoardIndex,
} from "crystal-draw";
import "crystal-draw/style.css";
import {
  HighlightOutlined,
  EditOutlined,
  DeleteOutlined,
  FontSizeOutlined,
  ArrowUpOutlined,
  CheckOutlined,
  LoadingOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import "./index.css";
import ArrowArcLeft from "Assets/Images/ArrowArcLeft.svg";
import ArrowArcRight from "Assets/Images/ArrowArcright.svg";

type ToolType = "pen" | "highlighter" | "eraser" | "text" | "select";
const COLORS: any = [
  "#ffffff", // White
  "#333333", // Dark Gray
  "#94a3b8", // Slate
  "#64748b", // Gray
  "#ef4444", // Red
  "#b91c1c", // Dark Red
  "#facc15", // Yellow
  "#a16207", // Olive/Dark Yellow
  "#22c55e", // Green
  "#15803d", // Dark Green
  "#06b6d4", // Cyan
  "#0891b2", // Dark Cyan
  "#3b82f6", // Blue
  "#1d4ed8", // Dark Blue
  "#d946ef", // Magenta
  "#a21caf", // Purple
];
const SIZES: any = [2, 4, 8, 16];

const TOOLS: { id: ToolType; icon: React.ReactNode; name: string }[] = [
  { id: "pen", icon: <EditOutlined />, name: "Pen" },
  { id: "highlighter", icon: <HighlightOutlined />, name: "Highlighter" },
  { id: "text", icon: <FontSizeOutlined />, name: "Text Tool" },
  { id: "eraser", icon: <DeleteOutlined />, name: "Eraser" },
  {
    id: "select",
    icon: <ArrowUpOutlined style={{ transform: "rotate(-45deg)" }} />,
    name: "Select",
  },
];

const hexToRgba = (hex: string, opacity: number) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
        result[3],
        16
      )}, ${opacity})`
    : hex;
};

const PdfAnnotator = forwardRef(({ pdfUrl }: { pdfUrl?: string }, ref) => {
  const [activeTool, setActiveTool] = useState<any>("pen");
  const [activeColor, setActiveColor] = useState("#ef4444");
  const [activeSize, setActiveSize] = useState(4);
  const [showSettings, setShowSettings] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const currentBoardIndex = useBoundStore((state) => state.currentBoardIndex);
  const totalBoardsCount = useBoundStore((state) => state.boards.length);
  const handlePrevPage = () => {
    if (currentBoardIndex > 0) {
      setCurrentBoardIndex(currentBoardIndex - 1);
    }
  };

  const handleNextPage = () => {
    if (currentBoardIndex < totalBoardsCount - 1) {
      setCurrentBoardIndex(currentBoardIndex + 1);
    }
  };
  useImperativeHandle(ref, () => ({
    getAnnotatedPdf: async () => {
      const container: any = document.getElementById("crystal--draw-container");
      if (!container) return null;
      const exportedFile = await exportPDF(container);
      return exportedFile;
    },
    jumpToPage: (pageNumber: number) => {
      if (pageNumber > 0 && pageNumber <= totalBoardsCount) {
        setCurrentBoardIndex(pageNumber - 1);
      } else {
        console.warn(
          `Attempted to jump to invalid page ${pageNumber}. Total boards: ${totalBoardsCount}`
        );
      }
    },
  }));
  useEffect(() => {
    setTool("pen");
    setColor("#ef4444"); // Default to a visible red for evaluation
    setLineWidth(4, "PEN");

    // Automatically load the PDF on first render if pdfUrl is provided
    if (pdfUrl) {
      const loadInitialPdf = async () => {
        setIsPdfLoading(true);
        try {
          // Clear all existing boards from the global crystal-draw store
          // before loading a new PDF, to prevent pages from a previous
          // student's PDF from combining with the current one.
          const existingBoards = useBoundStore.getState().boards;
          existingBoards.forEach((board: any) => {
            removeBoard(board.id as number);
          });

          const response = await fetch(pdfUrl);
          const blob = await response.blob();
          const file = new File([blob], "student-answer.pdf", {
            type: "application/pdf",
          });
          console.log("Loading initial PDF from URL:", pdfUrl.split("/"), file);
          await uploadPDF(file);
        } catch (error) {
          console.error("Failed to load initial PDF:", error);
        } finally {
          setIsPdfLoading(false);
        }
      };
      loadInitialPdf();
    }
  }, [pdfUrl]);

  const handleToolChange = (toolId: ToolType) => {
    if (activeTool === toolId) {
      setShowSettings(!showSettings);
    } else {
      setActiveTool(toolId);
      setTool(toolId);
      setShowSettings(toolId === "pen" || toolId === "highlighter"); // Open automatically for pen/highlighter
      if (toolId === "highlighter") {
        setHighlighterColor(hexToRgba(activeColor, 0.3));
      } else if (toolId === "pen") {
        setColor(activeColor);
      }
    }
  };

  // const handleUploadPDF = async (e: ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (file) {
  //     await uploadPDF(file);
  //     e.target.value = "";
  //   }
  // };

  // const handleExportPDF = async () => {
  //   const container: any = document.getElementById("crystal--draw-container");
  //   if (!container) return;

  //   const exportedFile = await exportPDF(container);

  //   if (exportedFile) {
  //     const url = URL.createObjectURL(exportedFile);
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.download = `Evaluation-${new Date().getTime()}.pdf`;
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     URL.revokeObjectURL(url);
  //   }
  // };
  const handleColorChange = (color: string) => {
    setActiveColor(color);
    if (activeTool === "highlighter") {
      setHighlighterColor(hexToRgba(color, 0.3));
    } else {
      setColor(color);
    }
    setShowSettings(false);
  };
  const handleSizeChange = (size: any) => {
    setActiveSize(size);
    setLineWidth(size, activeTool.toUpperCase());
  };
  const CrystalDrawComponent = CrystalDraw as any;

  return (
    <div className="app-main">
      {/* Loading Overlay */}
      {isPdfLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(255, 255, 255, 0.75)",
            backdropFilter: "blur(4px)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            gap: "16px",
          }}
        >
          <Spin
            indicator={
              <LoadingOutlined
                style={{ fontSize: 36, color: "#1890ff" }}
                spin
              />
            }
          />
          <span
            style={{
              fontSize: "15px",
              fontWeight: 600,
              color: "#595959",
              letterSpacing: "0.2px",
            }}
          >
            Loading Student Answer Sheet...
          </span>
        </div>
      )}

      {/* Floating Contextual Settings (Split into Two Modals) */}
      {showSettings && (
        <div
          className="settings-container"
          style={{
            position: "absolute",
            left: "70px",
            top: "80px",
            zIndex: 105,
            display: "flex",
            gap: "12px",
            alignItems: "center",
          }}
        >
          {/* Size Modal */}
          <div
            className="contextual-settings"
            style={{
              position: "static",
              animation: "fadeInDown 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              height: "120px",
            }}
          >
            <div className="size-group">
              {SIZES.map((size: any) => (
                <div
                  key={size}
                  className={`size-option ${
                    activeSize === size ? "active" : ""
                  }`}
                  style={{ width: size + 4, height: size + 4 }}
                  onClick={() => handleSizeChange(size)}
                  title={`Size ${size}`}
                >
                  {activeSize === size && (
                    <CheckOutlined
                      style={{ fontSize: size / 2 + 2, color: "#fff" }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Color Modal */}
          <div
            className="contextual-settings"
            style={{
              position: "static",
              animation: "fadeInDown 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <div className="settings-header">
              <button
                className="close-btn"
                onClick={() => setShowSettings(false)}
              >
                ×
              </button>
            </div>
            <div className="color-group">
              {COLORS.map((color: string) => (
                <div
                  key={color}
                  className={`color-option ${
                    activeColor === color ? "active" : ""
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => handleColorChange(color)}
                  title={color}
                >
                  {activeColor === color && (
                    <CheckOutlined
                      style={{
                        fontSize: "10px",
                        color: color === "#ffffff" ? "#000" : "#fff",
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="toolbar-container">
        {/* Main Tool Selection */}
        <div className="tool-group">
          {TOOLS.map((tool) => (
            <button
              key={tool.id}
              className={`tool-button ${
                activeTool === tool.id ? "active" : ""
              }`}
              onClick={() => handleToolChange(tool.id)}
              title={tool.name}
            >
              {tool.icon}
            </button>
          ))}
        </div>

        {/* Edit History */}
        <div className="action-group" style={{ flexDirection: "row" }}>
          <button
            className="action-button"
            onClick={undo}
            title="Undo (Ctrl+Z)"
          >
            <img
              src={ArrowArcLeft}
              alt="Undo"
              style={{ width: "1em", height: "1em" }}
            />
          </button>
          <button
            className="action-button"
            onClick={redo}
            title="Redo (Ctrl+Y)"
          >
            <img
              src={ArrowArcRight}
              alt="Redo"
              style={{ width: "1em", height: "1em" }}
            />
          </button>
        </div>

        {/* File Operations */}
        {/* <div className="action-group">
          <label className="action-button" title="Upload Student PDF">
            <UploadOutlined />
            <input
              type="file"
              accept="application/pdf"
              style={{ display: "none" }}
              onChange={handleUploadPDF}
            />
          </label>
          <button
            className="action-button"
            onClick={handleExportPDF}
            title="Download Evaluated PDF"
          >
            <DownloadOutlined />
          </button>
        </div> */}

        {/* Board Management */}
        {/* <div className="action-group">
          <button
            className="add-board-button"
            onClick={addBoard}
            title="Add New Page"
          >
            <PlusOutlined />
          </button>
        </div> */}
      </div>

      <div className="annotator-workspace">
        <div className="boards-viewport">
          <CrystalDrawComponent />
        </div>

        {totalBoardsCount > 0 && (
          <div className="pagination-container">
            <button
              className="pagination-btn"
              onClick={handlePrevPage}
              disabled={currentBoardIndex <= 0}
              title="Previous Page"
            >
              <LeftOutlined />
            </button>
            <span className="pagination-info">
              Page {currentBoardIndex + 1} of {totalBoardsCount}
            </span>
            <button
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={currentBoardIndex >= totalBoardsCount - 1}
              title="Next Page"
            >
              <RightOutlined />
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

PdfAnnotator.displayName = "PdfAnnotator";

export default PdfAnnotator;
